import React, { useState, useEffect, useCallback, useRef } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa"; // Importing caret icons
import "./Custom-Calendar.css";
import { useFormContext } from "@/providers";
import { useLocation } from "react-router-dom";
import { chatServerInstanceWithToken } from "@/helpers";

import Swal from "sweetalert2";

const localizer = momentLocalizer(moment);

const CustomToolbar = ({ label, onNavigate }) => {
  return (
    <div className="flex items-center justify-between rbc-toolbar">
      <button
        className="p-2 rbc-btn"
        onClick={(e) => {
          e.preventDefault();
          onNavigate("PREV");
        }}
        title="Previous Month"
      >
        <FaChevronLeft />
      </button>

      <span className="text-2xl rbc-toolbar-label text-bold">{label}</span>

      <button
        className="p-2 rbc-btn"
        onClick={(e) => {
          e.preventDefault();
          onNavigate("NEXT");
        }}
        title="Next Month"
      >
        <FaChevronRight />
      </button>
    </div>
  );
};

export const CustomDateSelectionCalendar = () => {
  const location = useLocation();
  const { listId = "" } = location.state || {};

  const { setValue, errors, fieldRefs, watch } = useFormContext();
  const [events, setEvents] = useState([]);
  const [disabledDates, setDisabledDates] = useState([]);
  const [hasDatesChanged, setHasDatesChanged] = useState(false);
  const [url, setUrl] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const blocked_dates = watch("blocked_dates");
  const [initialBlockedDates, setInitialBlockedDates] = useState(null);
  const dropdownRef = useRef(null);
  const [file, setFile] = useState(null);

  // Track initial blocked_dates when component mounts or listId changes
  useEffect(() => {
    if (blocked_dates && !initialBlockedDates) {
      setInitialBlockedDates(blocked_dates);
    }
  }, []);

  // Update disabledDates when blocked_dates changes
  useEffect(() => {
    if (blocked_dates) {
      const parsedDates = [];
      blocked_dates.forEach(({ start_date, end_date }) => {
        const start = moment(start_date);
        const end = moment(end_date);
        while (start.isSameOrBefore(end)) {
          parsedDates.push(start.clone().toDate());
          start.add(1, "day");
        }
      });
      setDisabledDates(parsedDates);
    }
  }, []);

  // Check for changes in blocked_dates
  useEffect(() => {
    if (initialBlockedDates && blocked_dates) {
      const hasChanged = !(
        blocked_dates.length === initialBlockedDates.length &&
        blocked_dates.every(
          (date, index) =>
            moment(date.start_date).isSame(
              initialBlockedDates[index].start_date,
              "day"
            ) &&
            moment(date.end_date).isSame(
              initialBlockedDates[index].end_date,
              "day"
            )
        )
      );
      setHasDatesChanged(hasChanged);

      if (hasChanged) {
        setValue("has_dates_changed", hasChanged ? 1 : 0);
      }
    }
  }, []);

  // Utility function to get all the dates between two dates
  const getDatesInRange = useCallback((start, end) => {
    const dates = [];
    let currentDate = moment(start).startOf("day");
    while (currentDate.isSameOrBefore(moment(end).startOf("day"))) {
      dates.push(currentDate.toDate());
      currentDate.add(1, "day");
    }
    return dates;
  }, []);

  // Refactored customDayPropGetter to use useCallback
  const customDayPropGetter = useCallback(
    (date) => {
      const isPastDate = moment(date).isBefore(moment().startOf("day"), "day");
      const isBlocked = disabledDates.some((disabledDate) =>
        moment(disabledDate).isSame(date, "day")
      );

      return {
        className: isBlocked ? "blocked-day" : "",
        style: {
          backgroundColor: isPastDate
            ? "#f0f0f0"
            : isBlocked
            ? "#f8d7da"
            : "#ffffff",
          pointerEvents: isPastDate ? "none" : "auto",
          cursor: isPastDate ? "not-allowed" : "pointer",
          position: "relative",
        },
      };
    },
    [disabledDates]
  );

  // Refactored divideIntoRangesAndSingularDates to use useCallback
  const divideIntoRangesAndSingularDates = useCallback((dates) => {
    if (!dates.length) return { ranges: [] };

    const sortedDates = dates.sort((a, b) => new Date(a) - new Date(b));
    let ranges = [];
    let currentStartDate = moment(sortedDates[0]);
    let currentEndDate = moment(sortedDates[0]);

    for (let i = 1; i < sortedDates.length; i++) {
      let currentDate = moment(sortedDates[i]);

      if (currentDate.diff(currentEndDate, "days") === 1) {
        currentEndDate = currentDate;
      } else {
        ranges.push({
          start_date: currentStartDate.format("YYYY-MM-DD"),
          end_date: currentEndDate.format("YYYY-MM-DD"),
          type: "blocked",
        });
        currentStartDate = currentDate;
        currentEndDate = currentDate;
      }
    }

    ranges.push({
      start_date: currentStartDate.format("YYYY-MM-DD"),
      end_date: currentEndDate.format("YYYY-MM-DD"),
      type: "blocked",
    });

    return { ranges };
  }, []);

  // Refactored handleDateSelection to use useCallback
  const handleDateSelection = useCallback(() => {
    const sortedDates = disabledDates.sort((a, b) => new Date(a) - new Date(b));
    const { ranges } = divideIntoRangesAndSingularDates(sortedDates);
    setValue("blocked_dates", ranges);
  }, [disabledDates, divideIntoRangesAndSingularDates, setValue]);

  const handleSelectSlot = useCallback(
    ({ start, end }) => {
      if (moment(start).isBefore(moment().startOf("day"), "day")) {
        return;
      }

      const isRangeSelected = end && !moment(start).isSame(end, "day");
      let selectedDates = [];

      if (isRangeSelected) {
        selectedDates = getDatesInRange(start, end);
      } else {
        selectedDates = [start];
      }

      // Check if all selected dates are already blocked
      const allSelected = selectedDates.every((date) =>
        disabledDates.some((disabledDate) =>
          moment(disabledDate).isSame(date, "day")
        )
      );

      setDisabledDates((prevDisabledDates) => {
        if (allSelected) {
          // If all dates are blocked, unblock them
          return prevDisabledDates.filter(
            (date) =>
              !selectedDates.some((selectedDate) =>
                moment(date).isSame(selectedDate, "day")
              )
          );
        } else {
          // If not all dates are blocked, block all selected dates
          const newDates = [...prevDisabledDates];
          selectedDates.forEach((date) => {
            if (!newDates.some((d) => moment(d).isSame(date, "day"))) {
              newDates.push(date);
            }
          });
          return newDates;
        }
      });

      setEvents((prevEvents) => {
        if (allSelected) {
          return prevEvents.filter(
            (event) =>
              !selectedDates.some((selectedDate) =>
                moment(event.start).isSame(selectedDate, "day")
              )
          );
        } else {
          return [
            ...prevEvents,
            ...selectedDates.map((date) => ({
              start: date,
              end: date,
              title: "Blocked Date",
            })),
          ];
        }
      });
    },
    [getDatesInRange, disabledDates]
  );

  // Refactored toggleDropdown to use useCallback
  const toggleDropdown = useCallback(
    (e) => {
      e.preventDefault();
      setIsDropdownOpen(!isDropdownOpen);
    },
    [isDropdownOpen]
  );

  const isRangeBoundary = (date) => {
    // Convert the date to moment for consistency in comparison
    const testDate = moment(date).startOf("day"); // Ensure it's at the start of the day
    const sortedDates = disabledDates.sort((a, b) => new Date(a) - new Date(b));
    const { ranges } = divideIntoRangesAndSingularDates(sortedDates);
    // Check if the date is the start of any range
    const isStart = ranges.some((blocked) =>
      testDate.isSame(moment(blocked.start_date).startOf("day"), "day")
    );

    // Check if the date is the end of any range
    const isEnd = ranges.some((blocked) =>
      testDate.isSame(moment(blocked.end_date).startOf("day"), "day")
    );

    // Check if the date is inside any blocked range (not including the start or end)
    const isInRange = ranges.some((blocked) =>
      testDate.isBetween(
        moment(blocked.start_date).startOf("day"),
        moment(blocked.end_date).startOf("day"),
        "day",
        "[]"
      )
    );

    // Check if the date is inside a blocked range but not the start or end
    const isInRangeNotBoundary = ranges.some(
      (blocked) =>
        testDate.isBetween(
          moment(blocked.start_date).startOf("day"),
          moment(blocked.end_date).startOf("day"),
          "day",
          "[]"
        ) &&
        !testDate.isSame(moment(blocked.start_date).startOf("day"), "day") &&
        !testDate.isSame(moment(blocked.end_date).startOf("day"), "day")
    );

    return { isStart, isEnd, isInRange, isInRangeNotBoundary };
  };

  const handleSyncICS = useCallback(
    (event) => {
      event.preventDefault();
      console.log("Syncing with iCal...", url);
      setLoading(true);
      try {
        // Send the URL as a query parameter
        chatServerInstanceWithToken
          .get("/ical", { params: { url } })
          .then((response) => {
            const bookings = response?.data;

            let allDates = [];

            bookings.forEach((booking) => {
              const startDate = moment(booking.start);
              const endDate = moment(booking.end);

              // Use getDatesInRange to get all the dates between start and end
              const datesInRange = getDatesInRange(startDate, endDate);
              allDates = [...allDates, ...datesInRange];
            });

            // Use moment to format each date and remove duplicates
            const uniqueDates = [
              ...new Set(
                allDates.map((date) => moment(date).format("YYYY-MM-DD"))
              ),
            ];

            // Update the disabledDates state and React Hook Form field
            setDisabledDates((oldDates) => {
              // Combine old state with new unique dates
              const updatedDates = [
                ...new Set([...oldDates, ...blocked_dates, ...uniqueDates]),
              ];

              // Update react-hook-form's 'blocked_dates'
              // setValue('blocked_dates', updatedDates);

              // Return the updated state
              return updatedDates;
            });

            setLoading(false);
          })
          .catch((error) => {
            console.log("Error fetching bookings from iCal:", error);
            Swal.fire({
              icon: "error",
              text: error?.response?.data?.message,
            });

            setLoading(false);
          });
      } catch (error) {
        Swal.fire({
          icon: "error",
          text: error.message,
        });

        setLoading(false);
        console.error("Error syncing with iCal:", error);
      }
    },
    [blocked_dates, getDatesInRange, url]
  );

  // Refactored handleExportICS to use useCallback
  const handleExportICS = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        // Send the URL as a query parameter
        await chatServerInstanceWithToken
          .post("/ical/generate", {
            blocked_dates: [...blocked_dates, ...disabledDates],
          })

          .then((response) => {
            const fileUrl = response?.data;
            setFile(fileUrl?.icalUrl);
            // setModal(true);
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              text: error?.response?.data?.message,
            });
          });
      } catch (error) {
        Swal.fire({
          icon: "error",
          text: error.message,
        });
        console.error("Error syncing with iCal:", error);
      }
    },
    [blocked_dates, disabledDates]
  );

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false); // Close the dropdown if clicked outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside); // Listen for click events
    return () => {
      document.removeEventListener("mousedown", handleClickOutside); // Clean up the listener
    };
  }, []);

  useEffect(() => {
    if (disabledDates.length > 0) {
      handleDateSelection();
    }
  }, [disabledDates, handleDateSelection]);

  return (
    <div className="bg-white">
      <div className="relative flex justify-end w-full p-5 my-3 mt-4 text-left">
        <button
          onClick={toggleDropdown}
          className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-lg hover:bg-gray-50 focus:outline-none"
        >
          Sync iCal
        </button>
        {isDropdownOpen && (
          <div
            ref={dropdownRef} // Attach the ref here
            className="origin-top-right absolute right-[100px] top-[45px] mt-2 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[99]"
          >
            <div className="py-1">
              <div className="p-1">
                <div className="flex justify-between gap-2">
                  <div className="flex flex-col">
                    <div>
                      <button
                        onClick={handleExportICS}
                        className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                      >
                        Export to ICS
                      </button>
                    </div>
                    {file && (
                      <div className="max-w-[260px] p-2 bg-white rounded-md border border-gray-300">
                        <p className="mb-2 text-xs text-gray-600 break-words">
                          {file}
                        </p>
                        <a
                          href={file}
                          download
                          className="flex items-center justify-center gap-2 bg-[#54C4D9] text-white text-sm px-4 py-2 rounded-md transition shadow-md w-full"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 4v12m0 0l-3-3m3 3l3-3m-6 5h6m-6 0H6m6 0h6"
                            ></path>
                          </svg>
                          Download ICS
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-1">
                <label
                  htmlFor="importICS"
                  className="block w-full px-4 py-2 text-sm text-left text-gray-700 cursor-pointer"
                >
                  Import from ICS
                </label>
                <div className="flex justify-between gap-2 p-1">
                  <input
                    id="importICS"
                    type="url"
                    placeholder="Enter your ICS URL"
                    className="pl-3 border-soft"
                    onChange={(event) => setUrl(event.target.value)}
                  />
                  <button
                    className="btn-blue-500 ml-auto px-4 py-2 text-sm text-white bg-[#8ec639] text-left rounded-md"
                    onClick={handleSyncICS}
                  >
                    {loading ? "Syncing..." : "Sync"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Calendar
        localizer={localizer}
        // events={events}
        ref={(el) => (fieldRefs.current[`blocked_dates`] = el)}
        selectable
        onSelectSlot={handleSelectSlot}
        dayPropGetter={customDayPropGetter}
        style={{ height: 500, margin: "25px" }}
        defaultView="month"
        components={{
          toolbar: CustomToolbar,
          dateCellWrapper: ({ value }) => {
            const isBlocked = disabledDates.some((disabledDate) =>
              moment(disabledDate).isSame(value, "day")
            );

            const isToday = moment(value).isSame(moment(), "day");
            const isAvailable = moment(value).isSameOrAfter(
              moment().startOf("day"),
              "day"
            );
            const isInPast = moment(value).isBefore(
              moment().startOf("day"),
              "day"
            );

            const { isStart, isEnd } = isRangeBoundary(value);

            return (
              <div
                style={{ position: "relative", height: "100%", width: "100%" }}
              >
                {/* Skewed background for available dates */}
                {isAvailable && !isBlocked && !isToday && !isInPast && (
                  <>
                    <div
                      className="available-bg"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "#8ec639",
                        transformOrigin: "top left",
                        zIndex: 1,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: "4px",
                        left: "4px",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        zIndex: 2,
                      }}
                    >
                      Available
                    </div>
                  </>
                )}
                {/* Skewed background and label for blocked dates */}
                {isBlocked && !isToday && !isStart && !isEnd && !isInPast && (
                  <>
                    <div
                      className={`blocked-bg`} // Add classes based on isStart or isEnd
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        // backgroundColor: "red", // Red for blocked
                        opacity: 0.3, // Fully opaque for start/end, 30% for in-between
                        // transformOrigin: "top left",
                        zIndex: 1,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: "4px",
                        left: "4px",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        zIndex: 2,
                      }}
                    >
                      Blocked
                    </div>
                  </>
                )}

                {/* Skewed background and label for blocked dates */}
                {isStart && !isToday && !isInPast && (
                  <>
                    <div
                      className={`start-date`} // Add classes based on isStart or isEnd
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "red", // Red for blocked
                        opacity: 0.3, // Fully opaque for start/end, 30% for in-between
                        // transformOrigin: "top left",
                        zIndex: 1,
                      }}
                    />
                    <div
                      className={`start-date-prefill`} // Add classes based on isStart or isEnd
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        opacity: 0.3, // Fully opaque for start/end, 30% for in-between
                        zIndex: 1,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: "4px",
                        left: "4px",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        zIndex: 2,
                      }}
                    >
                      Blocked
                    </div>
                  </>
                )}

                {/* Skewed background and label for blocked dates */}
                {isEnd && !isToday && !isInPast && (
                  <>
                    <div
                      className={`end-date`} // Add classes based on isStart or isEnd
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "red", // Red for blocked
                        opacity: 0.3, // Fully opaque for start/end, 30% for in-between
                        // transformOrigin: "top left",
                        zIndex: 1,
                      }}
                    />
                    <div
                      className={`end-date-prefill`} // Add classes based on isStart or isEnd
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        opacity: 0.3, // Fully opaque for start/end, 30% for in-between
                        zIndex: 1,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: "4px",
                        left: "4px",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        padding: "2px 6px",
                        backgroundColor: "transparent",
                        borderRadius: "4px",
                        zIndex: 2,
                      }}
                    >
                      Blocked
                    </div>
                  </>
                )}

                {/* Label for today's date */}
                {/* {isToday && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            bottom: "4px",
                                            left: "4px",
                                            fontSize: "0.75rem",
                                            fontWeight: "bold",
                                            padding: "2px 6px",
                                            borderRadius: "4px",
                                            zIndex: 2,
                                        }}
                                    >
                                        Today
                                    </div>
                                )} */}
              </div>
            );
          },
        }}
      />

      {errors?.blocked_dates?.map((err) => {
        return <p style={{ color: "red" }}>{err?.message}</p>;
      })}
    </div>
  );
};
